import { CheckCircleFilled, DownOutlined, LoadingOutlined, UserOutlined } from "@ant-design/icons";
import { Steps, Tag, Typography } from "antd";
import type { ReactNode } from "react";
import type { SetupStep, StepKey, StepStatus } from "../../service/onboarding.service";
import styles from "./setup.module.scss";
import { OWNER_META, STATUS_META, STEP_COPY, formatDate } from "./setup.shared";

const { Paragraph, Text } = Typography;

/** Our five states onto the four Ant Steps states. */
const ANTD_STATUS: Record<StepStatus, "finish" | "process" | "wait" | "error"> = {
  complete: "finish",
  awaiting_customer: "process",
  in_progress_nudge: "process",
  blocked: "error",
  locked: "wait",
};

/** Ant renders "awaiting_customer" and "in_progress_nudge" identically; telling them apart is the point. */
function iconFor(step: SetupStep): ReactNode | undefined {
  if (step.status === "awaiting_customer") {
    return (
      <span className={`${styles.marker} ${styles.markerCustomer}`}>
        <UserOutlined />
      </span>
    );
  }
  if (step.status === "in_progress_nudge") {
    return (
      <span className={`${styles.marker} ${styles.markerNudge}`}>
        <LoadingOutlined />
      </span>
    );
  }
  return undefined;
}

type Props = {
  steps: SetupStep[];
  openStep: StepKey | null;
  onToggle: (key: StepKey) => void;
  /** Per-step body, keyed by step. */
  bodies: Record<StepKey, ReactNode>;
};

export default function SetupSteps({ steps, openStep, onToggle, bodies }: Props) {
  const items = steps.map((step) => {
    const meta = STATUS_META[step.status];
    const owner = OWNER_META[step.owner];
    const copy = STEP_COPY[step.key];
    const isOpen = step.key === openStep;
    const body = bodies[step.key];
    const timing = step.status === "complete" && step.completed_at ? formatDate(step.completed_at) : "";

    return {
      status: ANTD_STATUS[step.status],
      icon: iconFor(step),
      title: (
        <span className={styles.stepTitle}>
          <span className={styles.stepTitleLeft}>
            <span className={styles.stepTitleText}>{step.title}</span>
            <Tag bordered={false} className={styles.ownerTag} style={{ background: owner.bg, color: owner.fg }}>
              {owner.label}
            </Tag>
          </span>

          <span className={styles.stepTitleRight}>
            {timing ? (
              <Text type="secondary" className={styles.stepSubtitle}>
                {timing}
              </Text>
            ) : null}
            <span
              className={styles.statusPill}
              style={{ background: meta.bg, color: meta.fg, border: `1px solid ${meta.border}` }}
            >
              <span className={styles.statusDot} style={{ background: meta.dot }} />
              {meta.label}
            </span>
            {/* Affordance: without this nothing tells you the row opens. */}
            <DownOutlined className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} aria-hidden="true" />
          </span>
        </span>
      ),
      description: (
        <div className={styles.stepDescription}>
          {step.status_detail ? (
            <p className={styles.statusDetail} style={{ color: meta.fg }}>
              {step.status_detail}
            </p>
          ) : null}

          {isOpen ? (
            // rc-steps' clickable row wraps the description too, and it also toggles on
            // Space/Enter keydown — a space typed into a field collapsed the step.
            <div
              className={styles.stepBody}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === " " || event.key === "Enter") event.stopPropagation();
              }}
            >
              <Paragraph className={styles.blurb}>{copy.blurb}</Paragraph>
              {copy.nothingNeeded ? (
                <p className={styles.nothingNeeded}>
                  <CheckCircleFilled /> Nothing needed from you here
                </p>
              ) : null}
              {body}
            </div>
          ) : null}
        </div>
      ),
    };
  });

  return (
    <Steps
      className={styles.steps}
      direction="vertical"
      // -1 on purpose: rc-steps ignores a click on the step equal to `current`.
      current={-1}
      // Passing onChange is also what makes the rows clickable at all.
      onChange={(index) => {
        const step = steps[index];
        if (step) onToggle(step.key);
      }}
      items={items}
    />
  );
}
